import React from 'react';
import {
  ItemListConfig,
  ItemValueType,
  Section
} from '../../../../../../Models';
import {
  checkSectionsFields,
  getSectionsDefaultValue,
  handleNeedHelpScroll,
  getFirstInvalidFieldPath
} from '../../../../../../Helpers';
import { useNeedHelpTarget } from '../../Contexts/NeedHelpContext';
import {
  getUserGroups,
  isEqualObject,
  UserGroup
} from 'ui-smartforce-settings';
import { APP_SETTINGS_API_BASE_URL } from '../../../../../../Constants/Url';

export interface UseItemListFormViewParams {
  config: ItemListConfig;
  itemListValue: ItemValueType[];
  sectionName?: string;
  onChange: (itemListValue: ItemValueType[], index?: number) => void;
}

export interface UseItemListFormViewResult {
  isPanelOpen: boolean;
  value: ItemValueType;
  selected: number;
  groups: UserGroup[];
  isSaveDisabled: boolean;
  isItemMissingInformation: boolean;
  onAdd: () => void;
  onSelect: (index: number) => void;
  onRemove: (index: number) => void;
  onDuplicate: (index: number) => void;
  onSave: () => void;
  onClosePanel: () => void;
  onItemFormChange: (value: ItemValueType) => void;
  onNeedHelp: () => void;
}

export const useItemListFormView = ({
  config,
  itemListValue,
  sectionName,
  onChange
}: UseItemListFormViewParams): UseItemListFormViewResult => {
  const { setNeedHelpTargetPath } = useNeedHelpTarget();
  const [isPanelOpen, setIsPanelOpen] = React.useState<boolean>(false);

  const [value, setValue] = React.useState<ItemValueType>(
    getSectionsDefaultValue(config.sections as Section[])
  );

  const [selected, setSelected] = React.useState<number>(-1);
  const [groups, setGroups] = React.useState<UserGroup[]>([]);

  const onAdd = () => {
    const sectionDefaultValue = getSectionsDefaultValue(
      config.sections as Section[]
    );

    //Check if its neccessary to reuse first item fields data
    if (config.reuse_fields && itemListValue.length > 0) {
      //Initialize the value for the next item with defaults
      const nextValue = { ...sectionDefaultValue };

      //Loop the fields to reuse in config
      for (let fieldString of config.reuse_fields) {
        const [fieldSection, fieldName] = fieldString.split('.');

        //If the first item has a value for that field, reuse it
        if (itemListValue[0][fieldSection][fieldName]) {
          //Initialize section if its empty
          if (!nextValue[fieldSection]) {
            nextValue[fieldSection] = {};
          }

          //Reuse field value from the first item
          nextValue[fieldSection][fieldName] =
            itemListValue[0][fieldSection][fieldName];
        }
      }

      setValue(nextValue);
    } else {
      setValue(sectionDefaultValue);
    }

    setIsPanelOpen(true);
  };

  const onSelect = (index: number): void => {
    setSelected(index);
    setValue(itemListValue[index]);
    setIsPanelOpen(true);
  };

  const onDuplicate = (index: number): void => {
    const selected = itemListValue[index];

    if (!selected) return;

    onChange([...itemListValue, selected]);
  };

  const onRemove = (index: number) => {
    let newList: ItemValueType[] = itemListValue.filter((_, i) => i !== index);

    onChange(newList, index);
  };

  const onSave = () => {
    // Is a new itemList
    if (selected === -1) {
      onChange([...itemListValue, value]);
    } else {
      onChange(
        [
          ...itemListValue.slice(0, selected),
          value,
          ...itemListValue.slice(selected + 1)
        ],
        selected
      );
    }

    onClosePanel();
  };

  const onClosePanel = () => {
    setNeedHelpTargetPath(null);
    setIsPanelOpen(false);
    setSelected(-1);
  };

  const onItemFormChange = (value: ItemValueType) => {
    setValue(value);
  };

  const onNeedHelp = () => {
    const cb = (path: string) => {
      setNeedHelpTargetPath(path);
    };

    handleNeedHelpScroll(config, value, sectionName, cb);
  };

  const isSaveDisabled: boolean =
    !checkSectionsFields(config, value) ||
    (selected !== -1 &&
      isEqualObject<ItemValueType>(itemListValue[selected], value));

  const isItemMissingInformation =
    selected !== -1 && !checkSectionsFields(config, value);

  React.useEffect(() => {
    let isSubscribed: boolean = true;
    const updateGroups = async () => {
      if (value.officer_information?.officer_name) {
        const groups = await getUserGroups(
          APP_SETTINGS_API_BASE_URL,
          value.officer_information.officer_name as string,
          value.officer_information.id as string | undefined
        );

        if (isSubscribed) {
          setGroups(groups);
        }
      }
    };

    if (value.officer_information?.officer_name) {
      updateGroups();
    } else {
      setGroups([]);
    }

    return () => {
      isSubscribed = false;
    };
  }, [value.officer_information?.officer_name, value.officer_information?.id]);

  // Now itemListValue could be modified from the parent
  React.useEffect(() => {
    if (selected === -1) {
      return;
    }

    const selectedItem = itemListValue[selected];

    if (selectedItem) {
      setValue(selectedItem);
    } else {
      setIsPanelOpen(false);
      setSelected(-1);
    }
  }, [selected, itemListValue]);

  // Clear help target when value becomes fully valid
  React.useEffect(() => {
    const isStillInvalid = getFirstInvalidFieldPath(config, value);
    if (!isStillInvalid) {
      setNeedHelpTargetPath(null);
    }
  }, [value, config, setNeedHelpTargetPath]);

  return {
    isPanelOpen,
    value,
    selected,
    groups,
    isSaveDisabled,
    isItemMissingInformation,
    onAdd,
    onSelect,
    onDuplicate,
    onRemove,
    onSave,
    onClosePanel,
    onItemFormChange,
    onNeedHelp
  };
};
